import {
    createBillsTaskViewConfig,
    createCounterpartiesViewConfig,
    createInvoicesViewConfig,
    createFinancialDocumentsViewConfig,
    createTransactionsViewConfig,
    readCloseTemplateStickyViewType,
} from "@common/helpers/close-template-task-view-config";
import { isStickyTaskType } from "@common/constants/taskTypeDefinitions";
import { TaskType } from "@common/types/task";
import { PersistableView } from "@common/types/views/views";

export interface PreviewTransaction {
    id: number;
    entity: { id: number };
}

interface SelectionPreviewParams {
    viewConfig?: PersistableView;
    transactions?: readonly PreviewTransaction[];
    transactionsCount?: number;
}

interface TaskPreviewDefinition {
    hasTemplatePreview: boolean;
    /** The template preview lists the rows the task would show for a client. */
    hasDataPreview: boolean;
    hasWorkspacePreview: boolean;
    hasSelectionPreview: (params: SelectionPreviewParams) => boolean;
    selectionViewOwnsEntities: boolean;
    isEntityChangeHandledByPreview?: boolean;
    supportsSplitOptions?: boolean;
    createDefaultView?: () => PersistableView;
    isTemplateSticky?: (viewConfig: object | null) => boolean;
    scopeViewToEntities?: (
        view: PersistableView,
        entityIds: number[] | undefined,
    ) => PersistableView;
}

export const isCounterpartiesSelectionView = (
    viewConfig: PersistableView | undefined,
): boolean => !!viewConfig?.filters.counterpartiesIds?.length;

const hasViewConfig = ({ viewConfig }: SelectionPreviewParams): boolean =>
    !!viewConfig;

const hasNoSelectionPreview = (): boolean => false;

const withViewEntityIds = (
    view: PersistableView,
    entityIds: number[] | undefined,
): PersistableView => ({
    ...view,
    filters: {
        ...view.filters,
        entityIds: entityIds?.length ? entityIds : undefined,
    },
});

const withDocumentViewEntityIds = (
    view: PersistableView,
    entityIds: number[] | undefined,
): PersistableView =>
    view.filters.financialDocumentIds?.length
        ? view
        : withViewEntityIds(view, entityIds);

const TASK_PREVIEW_DEFINITIONS: Partial<
    Record<TaskType, TaskPreviewDefinition>
> = {
    [TaskType.TRANSACTIONS]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: true,
        hasSelectionPreview: ({
            viewConfig,
            transactions,
            transactionsCount,
        }) => !!viewConfig && (!!transactions?.length || !!transactionsCount),
        selectionViewOwnsEntities: true,
        isEntityChangeHandledByPreview: true,
        supportsSplitOptions: true,
        createDefaultView: () => createTransactionsViewConfig(),
        isTemplateSticky: (viewConfig) =>
            readCloseTemplateStickyViewType(viewConfig) !== "dynamic",
        scopeViewToEntities: withViewEntityIds,
    },
    [TaskType.TRANSACTION_REVIEW]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: false,
        hasSelectionPreview: hasNoSelectionPreview,
        selectionViewOwnsEntities: false,
    },
    [TaskType.PROFIT_LOSS]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: false,
        hasSelectionPreview: hasNoSelectionPreview,
        selectionViewOwnsEntities: false,
    },
    [TaskType.BALANCE_SHEET]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: false,
        hasSelectionPreview: hasNoSelectionPreview,
        selectionViewOwnsEntities: false,
    },
    [TaskType.FORM_1099]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: false,
        hasSelectionPreview: hasNoSelectionPreview,
        selectionViewOwnsEntities: false,
    },
    [TaskType.COUNTERPARTIES]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: true,
        hasSelectionPreview: ({ viewConfig }) =>
            isCounterpartiesSelectionView(viewConfig),
        selectionViewOwnsEntities: false,
        isEntityChangeHandledByPreview: true,
        createDefaultView: () => createCounterpartiesViewConfig(),
        isTemplateSticky: () => isStickyTaskType(TaskType.COUNTERPARTIES),
        scopeViewToEntities: withViewEntityIds,
    },
    [TaskType.BILLS]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: true,
        hasSelectionPreview: hasViewConfig,
        selectionViewOwnsEntities: false,
        createDefaultView: () => createBillsTaskViewConfig(),
        isTemplateSticky: () => true,
        scopeViewToEntities: withViewEntityIds,
    },
    [TaskType.INVOICES]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: true,
        hasSelectionPreview: hasViewConfig,
        // The pinned invoices carry their own entities; picking another entity
        // in the form must not empty the task.
        selectionViewOwnsEntities: true,
        isEntityChangeHandledByPreview: true,
        createDefaultView: () => createInvoicesViewConfig(),
        isTemplateSticky: () => true,
        scopeViewToEntities: withViewEntityIds,
    },
    [TaskType.FINANCIAL_DOCUMENTS]: {
        hasTemplatePreview: true,
        hasDataPreview: true,
        hasWorkspacePreview: true,
        hasSelectionPreview: hasViewConfig,
        selectionViewOwnsEntities: false,
        createDefaultView: () => createFinancialDocumentsViewConfig(),
        isTemplateSticky: () => true,
        scopeViewToEntities: withDocumentViewEntityIds,
    },
};

export const getTaskPreviewDefinition = (
    type: TaskType | null,
): TaskPreviewDefinition | undefined =>
    type === null ? undefined : TASK_PREVIEW_DEFINITIONS[type];

export const hasTaskDataPreview = (type: TaskType | null): boolean =>
    getTaskPreviewDefinition(type)?.hasDataPreview ?? false;
